/* eslint-disable no-console */
/* eslint-disable no-underscore-dangle */
import { ToolOptions, Wrap, ChangeCursor } from './styles';

import {
  ElipseIcon,
  LineIcon,
  PolygonIcon,
  SelectIcon,
} from './components/Icons';

import ButtonTooltip from '../../ButtonTooltip';

/**
 * Turf é uma coleção de módulos com algoritmos geográficos.
 */
import * as turf from '@turf/turf';
/**
 * Importação da biblioteca leaflet a qual utiliza o OpenStreetMap,
 * é uma biblioteca de mapeamento leve e de codigo aberto, utiliza um banco
 * de dados geografico editável gratuito.
 */
import L from 'leaflet';
/**
 * useState é um hook que nos permite rastrear o estado em um
 * componente de função, o estado geralmente se refere a dados ou
 * propriedads que precisam ser rastreador em um aplicativo.
 * 
 * useEffect é um hook que permite executar efeitos colaterais em
 * seus componentes, por exemplo: buscar dados, atualizar diretamente
 * o DOM e cronômetros, ele aceita dois argumentos o segundo é opcional.
 * 
 * useRef permite que persista valores entre renderizações, ele pode
 * ser usado para armazenar um valor mutável que não causa uma nova
 * renderização quando atualizado e pode ser usado para acessar di-
 * retamente um elemento do DOM.
 * 
 * useCallback é um hook que retorna uma função de retorno
 * de chamada memorizada
 */
import { useState, useEffect, useRef, useCallback } from 'react';
/**
 *  useMap é um hook personalizado criado para gerenciar a comunicação
 *  dos componentes com o mapa.
 */
import { useMap } from 'views/MapView/hooks';

const defaultStyle = {
  color: '#3C71DD',
  weight: 1.5,
  opacity: 1.0,
  lineCap: 'round',
  lineJoin: 'round',
  dashArray: '4',
  dashOffset: null,
  fillOpacity: 0,
  fillRule: 'evenodd',
};

/**
 * Funcionalidade de Selecionar Feição, nela é possivel selecionar uma 
 * area atraves de um poligono, mão livre, raio ou uma area quadrada arras-
 * tando o mouse, para habilitar a ferramenta é nescessario clicar em uma
 * camada importada ou uma camada criada. 
 * A funcionalidade nao funciona em camadas do catalogo!
 * Apos selecionada a area as feiçoes ficam destacadas tambem na tabela de 
 * atributos.
 * 
 * @param {string} mapContext Context
 * @param {boolean} isActive Verificação
 * @param {boolean} setIsActive Setar o estado
 * @param {*} props Resto 
 * 
 * @returns As opçoes para selecionar a feição(area quadrada arrastando o mouse,
 * mão livre, poligo ou raio). 
 */
export const SelectFeature = ({
  mapContext,
  isActive,
  setIsActive,
  ...props
}) => {
  const [activeTool, setActiveTool] = useState(null);
  const [anchorToolOptions, setAnchorToolOptions] = useState(null);
  const [layer, setLayer] = useState(null);

  const ctrlKeyRef = useRef(null);
  const shiftKeyRef = useRef(null);
  const mouseMoving = useRef(null);
  const { map, setLayers, layers } = useMap();

  /**
   * Determina se as features da camada selecionada intersecta com a area de seleção
   * e as adiciona a lista de features selecionadas
   * 
   * @param {*} selectionFeature Área de seleção
   * @param {boolean} unselectDuplicated
   */
  const selectFeatures = useCallback(
    (selectionFeature, unselectDuplicated = false) => {            
      let intersectedFeatures = [];
      let selectionBbox = turf.bbox(selectionFeature);
      selectionBbox = turf.bboxPolygon(selectionBbox);
      layer.leafletLayer.eachLayer((feature) => {        
        let featureBbox = feature.getBounds && feature.getBounds();
        featureBbox = featureBbox && [
          featureBbox.getSouthWest().lng,
          featureBbox.getSouthWest().lat,
          featureBbox.getNorthEast().lng,
          featureBbox.getNorthEast().lat,
        ];
        if (
          turf.booleanIntersects(
            selectionBbox,
            featureBbox ? turf.bboxPolygon(featureBbox) : feature.feature
          )
        ) {          
          turf.booleanIntersects(feature.feature, selectionFeature) &&
            intersectedFeatures.push(feature.feature);
        }
      });
      layer.setSelectedFeatures(
        intersectedFeatures,
        null,
        ctrlKeyRef.current,
        shiftKeyRef.current,        
        unselectDuplicated
      );
      setLayers([...layers]);    
    },
    [layer, layers, setLayers]
  );

  /**
   * Habilita pan somente com o botão do meio do mouse
   */
  const mouseWheelDragOnly = useCallback(() => {
    map.dragging._draggable._onDownOrig = map.dragging._draggable._onDown;
    map.dragging.removeHooks();
    map.dragging._draggable._onDown = function (e) {
      if (e.button !== 1) return;
      this._onDownOrig(e);
    };
    map.dragging.addHooks();
  }, [map.dragging]);

  /**
   * Reseta pan para o comportamento default
   */
  const resetDragBehavior = useCallback(() => {
    map.dragging.removeHooks();
    map.dragging._draggable._onDown = map.dragging._draggable._onDownOrig;
    map.dragging.addHooks();
  }, [map.dragging]);

  /**
   * Callback executado ao finalizar as inserções das geometrias de seleção
   */
  const onCreateFeature = useCallback(
    (e) => {
      if (e.layer) {
        e.layer.remove();
        let selectionFeature =
          e.layer instanceof L.Circle
            ? turf.circle(
                [e.layer.getLatLng().lng, e.layer.getLatLng().lat],
                e.layer.getRadius(),
                { units: 'meters' }
              )
            : e.layer.toGeoJSON();

        selectionFeature && selectFeatures(selectionFeature);
      }
    },
    [selectFeatures]
  );

  /**
   * Possibilita o desenho de uma geometria ao manter o mouse pressionado,
   * se a opção de seleção ou mão livre estiver habilitada.
   *
   * Deve ser adicionada ao evento de mousedown no mapa do leaflet
   */
  const drawLayerOnMouseDown = useCallback(
    ({ latlng, originalEvent }) => {      
      if (originalEvent.button === 1 || originalEvent.button === 2) return;
      const initialPosition = latlng;
      const coords = [initialPosition];
      let lastPosition = null;
      let drawedLayer = null;
      mouseMoving.current = false;

      const redrawLayer = (addToMap = true) => {
        if (activeTool === 'Selection' && lastPosition) {
          drawedLayer?.remove();
          drawedLayer = L.rectangle([initialPosition, lastPosition], {
            ...defaultStyle,
          });
          addToMap && drawedLayer.addTo(map);
          return drawedLayer;
        } else if (activeTool === 'HandsFree') {
          if (coords.length > 2) {
            drawedLayer?.remove();
            drawedLayer = L.geoJSON(
              turf.polygon([
                [...coords, initialPosition].map((c) => [c.lng, c.lat]),
              ]),
              { style: { ...defaultStyle } }
            );
            addToMap && drawedLayer.addTo(map);
            return drawedLayer;
          }
        }
        return null;
      };

      const onMouseMove = (e) => {        
        mouseMoving.current = true;
        coords.push(e.latlng);
        lastPosition = e.latlng;
        redrawLayer();
      };

      const onMouseUp = (e) => {
        setTimeout(() => (mouseMoving.current = false), 100);
        coords.push(e.latlng);
        lastPosition = mouseMoving.current ? e.latlng : null;
        onCreateFeature({ layer: redrawLayer(false), shape: 'Polygon' });

        map.off('mousemove', onMouseMove);
        map.off('mouseup', onMouseUp);
      };

      map.on('mousemove', onMouseMove);
      map.on('mouseup', onMouseUp);
    },
    [map, activeTool, onCreateFeature]
  );

  /** EFFECTS */

  /**
   * Habilita ferramenta de acordo com a camada selecionada
   */
  useEffect(() => {
    let selectedLayers = layers.filter((l) => l.isSelected && l.visible);
    if (
      selectedLayers.length === 1 &&
      (['importada', 'criada'].includes(selectedLayers[0].type) ||
        selectedLayers[0].getService() === 'WFS')
    ) {
      setLayer(selectedLayers[0]);
    } else {
      setLayer(null);
    }
  }, [layers]);

  /**
   * Desabilita ferramenta caso não haja camada selecionada
   */
  useEffect(() => {
    if (!layer && isActive) {
      setIsActive(false);
    }
  }, [layer, isActive, setIsActive]);

  /**
   * Reseta ferramenta ao ser desabilitada
   */
  useEffect(() => {    
    if (!isActive) {      
      setActiveTool(null);
      map.pm.disableDraw();
    }    
  }, [isActive, map]);

  /**
   * Verifica se a tecla ctrl está sendo pressionada
   */
  useEffect(() => {
    const onKeyDown = (e) =>
      (ctrlKeyRef.current = e.keyCode === 17 ? true : ctrlKeyRef.current);
    const onKeyUp = (e) =>
      (ctrlKeyRef.current = e.keyCode === 17 ? false : ctrlKeyRef.current);

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  /**
   * Verifica se a tecla Shift está sendo pressionada
   */
  useEffect(() => {
    const onKeyDown = (e) =>
      (shiftKeyRef.current = e.keyCode === 16 ? true : shiftKeyRef.current);
    const onKeyUp = (e) =>
      (shiftKeyRef.current = e.keyCode === 16 ? false : shiftKeyRef.current);

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  /**
   * Realiza ações específicas de acordo com a ferramenta ativa em 'activeTool'
   */
  useEffect(() => {
    // Opções para a inserção de geometrias com o Geoman
    const geomanOptions = {
      snappable: false,
      allowSelfIntersection: false,
      snapDistance: 20,
      finishOn: 'dblclick',
      cursorMarker: false,
      templineStyle: { ...defaultStyle },
      hintlineStyle: { ...defaultStyle },
      continueDrawing: true,
    };

    // Ações que dever ser realizadas antes de inicializar cada ferramenta
    const beforeAction = () => {
      map.pm.disableDraw();
    };

    switch (activeTool) {
      case 'Selection': {
        beforeAction();
        // Habilitando pan somente com o botão do meio do mouse
        mouseWheelDragOnly();

        // Intersecta geometrias no ponto clicado
        const onClickOnMap = ({ latlng, layerPoint }) => {          
          // Realiza buffer na area clicada para garantir a seleção
          const pxDistanceBuffer = 3;
          const measureCoord = map.layerPointToLatLng(
            L.point(layerPoint.x + pxDistanceBuffer, layerPoint.y)
          );
          const distanceBuffer = map.distance(latlng, measureCoord);
          const bufferedMarker = turf.buffer(
            turf.point([latlng.lng, latlng.lat]),
            distanceBuffer,
            { units: 'meters' }
          );

          selectFeatures(bufferedMarker, true);
        };
        map.on('click', onClickOnMap);
        map.on('mousedown', drawLayerOnMouseDown);
        return () => {
          map.off('click', onClickOnMap);
          map.off('mousedown', drawLayerOnMouseDown);
          // Resetando pan para o comportamento original
          resetDragBehavior();
        };
      }
      case 'Polygon': {
        beforeAction();
        map.on('pm:create', onCreateFeature);
        map.pm.enableDraw('Polygon', geomanOptions);

        return () => map.off('pm:create', onCreateFeature);
      }
      case 'Circle': {
        beforeAction();
        map.on('pm:create', onCreateFeature);
        map.pm.enableDraw('Circle', geomanOptions);
        return () => map.off('pm:create', onCreateFeature);
      }
      case 'HandsFree': {
        beforeAction();
        // Habilitando pan somente com o botão do meio do mouse
        mouseWheelDragOnly();

        map.on('mousedown', drawLayerOnMouseDown);
        return () => {
          map.off('mousedown', drawLayerOnMouseDown);
          // Resetando pan para o comportamento original
          resetDragBehavior();
        };
      }
      default:
        beforeAction();
        return;
    }
  }, [
    activeTool,
    map,
    layer,
    selectFeatures,
    mouseWheelDragOnly,
    resetDragBehavior,
    drawLayerOnMouseDown,
    onCreateFeature,
  ]);

  /**
   * Cria layer temporária para destacar as feições selecionadas
   */
  const handleSelectTool = (tool) => {    
    setActiveTool(activeTool === tool ? null : tool);
  };

  return (
    <Wrap>
      {activeTool && <ChangeCursor />}
      <ButtonTooltip
        tooltip={SelectFeature.properties.alias}
        description={SelectFeature.properties.description}
        onClick={(e) => {
          setIsActive(!isActive);
          setAnchorToolOptions(isActive ? null : e.currentTarget);
        }}
        tooltipDisabled={isActive}
        active={isActive}
        disabled={!layer}
      >
        <SelectIcon style={{ verticalAlign: 'middle' }} />
      </ButtonTooltip>
      <ToolOptions open={isActive} anchorEl={anchorToolOptions}>
        <ButtonTooltip
          tooltip="Selecionar feição"
          onClick={() => {
            handleSelectTool('Selection');
          }}
          active={activeTool === 'Selection'}
          activeWithBgColor
          // disabled={![null, 'Selection'].includes(toolState.selectedFeature)}
        >
          <SelectIcon />
        </ButtonTooltip>
        <ButtonTooltip
          tooltip="Selecionar feição por polígono"
          onClick={() => {
            handleSelectTool('Polygon');
          }}
          active={activeTool === 'Polygon'}
          activeWithBgColor
          // disabled={
          //   ![null, 'Polygon', 'Circle'].includes(toolState.selectedFeature)
          // }
        >
          <PolygonIcon />
        </ButtonTooltip>
        <ButtonTooltip
          tooltip="Selecionar feição a mão livre"
          onClick={() => {
            handleSelectTool('HandsFree');
          }}
          active={activeTool === 'HandsFree'}
          activeWithBgColor
          // disabled={![null, 'HandsFree'].includes(toolState.selectedFeature)}
        >
          <LineIcon />
        </ButtonTooltip>
        <ButtonTooltip
          tooltip="Selecionar feição por raio"
          onClick={() => {
            handleSelectTool('Circle');
          }}
          active={activeTool === 'Circle'}
          activeWithBgColor
          // disabled={
          //   ![null, 'Polygon', 'Circle'].includes(toolState.selectedFeature)
          // }
        >
          <ElipseIcon />
        </ButtonTooltip>
      </ToolOptions>
    </Wrap>
  );
};

SelectFeature.properties = {
  id: 'CPN-TB-A-SELECT-FEATURE',
  alias: 'Seleção de feição pelo mapa',
  description: 'Realiza a seleção de feição(ões). Selecionando uma camada criada ou importada e escolhendo umas das formas de seleção por clique, poligono, raio e mão livre.',
};

export default SelectFeature;
